/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select, Checkbox, Spin } from 'antd'
import type { SelectProps } from 'antd'
import React from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'

type SelectInputSize = 'small' | 'default'

/**
 * Control height per size — the same pair `HeaderActionButton` uses, so a
 * `small` select lines up with the compact buttons sharing its row.
 */
const SELECT_HEIGHT: Record<SelectInputSize, number> = {
  default: 40,
  small: 32,
}

/*
 * The antd theme sizes the INSIDE of a select for the 40px control: the
 * selector is 40px and the value's line-height 38px (40 minus the 1px top and
 * bottom borders). Shrinking only the outer box would leave the value sitting
 * low, so the compact size resets the inner parts to match.
 */
const COMPACT_SELECT_INNER = `
  .ant-select-selector {
    height: ${SELECT_HEIGHT.small}px !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: ${SELECT_HEIGHT.small - 2}px !important;
  }
  .ant-select-selection-search-input {
    height: ${SELECT_HEIGHT.small - 2}px !important;
  }
`

type SelectInputProps = {
  label: React.ReactNode | string
  disabled: boolean
  options: any[]
  name: string
  value: string
  onChange: (value: string) => void
  fieldErrors: any
  customError: any
  showSearch?: boolean
  filterOption?: ((input: string, option: any) => boolean) | false
  style?: any
  extraCheckBox?: boolean
  extraCheckBoxLabel?: string
  extraCheckBoxOnChange?: (value: boolean) => void
  onSearch?: (value: string) => void
  allowClear?: boolean
  inputRef?: React.RefObject<any>
  onSelect?: (value: string) => void
  placeholder?: string
  notFoundContent?: React.ReactNode
  /** Tab index for keyboard navigation */
  tabIndex?: number
  backgroundColor?: string
  /** Whether dropdown width should match select width. Set to false to let dropdown expand to fit content */
  popupMatchSelectWidth?: boolean
  /** Custom render function for the selected label display */
  labelRender?: (props: {
    label: React.ReactNode
    value: string
  }) => React.ReactNode
  /** Callback when dropdown is scrolled - useful for infinite scroll pagination */
  onPopupScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  /** Show loading spinner in dropdown */
  loading?: boolean
  /** Controls whether the dropdown is open */
  open?: boolean
  /** Callback when dropdown visibility changes */
  onDropdownVisibleChange?: (open: boolean) => void
  /** Callback when selection is cleared */
  onClear?: () => void
  /** Custom suffix icon for the select */
  suffixIcon?: React.ReactNode
  /** Callback for keyboard events */
  onKeyDown?: (e: React.KeyboardEvent) => void
  /** Focus the select on mount */
  autoFocus?: boolean
  /** `small` is the 32px compact control; defaults to the 40px form field */
  size?: SelectInputSize
  /**
   * `large` sets THIS field's label at body size (16px), for a dialog whose copy
   * is at body size. Default is the house 14px.
   */
  labelSize?: 'default' | 'large'
  /**
   * Draws each option in the open list (antd's `optionRender`) — two lines, a
   * check on the chosen one. The closed value is `labelRender`'s.
   */
  optionRender?: SelectProps['optionRender']
  /**
   * A class on THIS select's dropdown. The `<style>` block below is global, so an
   * option list styled differently is scoped by this class, never by editing it.
   */
  popupClassName?: string
  /**
   * Keep the label for assistive tech only (it becomes the select's aria-label), as
   * `TextInput`'s does. For a select whose label is shown elsewhere — a grid cell's
   * column header — where a label above the field would not fit. `label` must then be
   * a string.
   */
  hideLabel?: boolean
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  disabled,
  options,
  name,
  value,
  onChange,
  fieldErrors,
  customError,
  showSearch = false,
  filterOption,
  style,
  extraCheckBox,
  extraCheckBoxLabel,
  extraCheckBoxOnChange,
  onSearch,
  allowClear = true,
  inputRef,
  onSelect,
  placeholder,
  notFoundContent,
  tabIndex,
  backgroundColor = colors.SECONDARY_200,
  popupMatchSelectWidth = false,
  labelRender,
  onPopupScroll,
  loading,
  open,
  onDropdownVisibleChange,
  onClear,
  suffixIcon,
  onKeyDown,
  autoFocus,
  size = 'default',
  labelSize = 'default',
  optionRender,
  popupClassName,
  hideLabel = false,
}) => {
  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${backgroundColor} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      {hideLabel ? null : (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            align-self: stretch;
          `}
        >
          <Text
            variant={
              labelSize === 'large' ? 'paragraphMedium' : 'paragraphSmall'
            }
            weight='regular'
            color={colors.WHITE}
            style={`
            align-self: stretch;
            overflow-wrap: break-word;
          `}
          >
            {label}
          </Text>
          {extraCheckBox && extraCheckBoxLabel && extraCheckBoxOnChange && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                align-self: flex-end;
              `}
            >
              <Checkbox
                onChange={(e) => extraCheckBoxOnChange(e.target.checked)}
                css={css`
                  .ant-checkbox-inner {
                    background-color: ${colors.SECONDARY_200};
                    border-color: ${colors.WHITE};
                  }
                  .ant-checkbox-wrapper {
                    color: ${colors.WHITE};
                  }
                `}
              >
                {extraCheckBoxLabel}
              </Checkbox>
            </div>
          )}
        </div>
      )}
      <Select
        // rc-select hands aria attributes to its combobox input.
        aria-label={hideLabel && typeof label === 'string' ? label : undefined}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        popupMatchSelectWidth={popupMatchSelectWidth}
        placeholder={placeholder}
        ref={inputRef}
        allowClear={allowClear}
        disabled={disabled}
        showSearch={showSearch}
        onChange={(e) => onChange(e)}
        onSearch={(e) => onSearch && onSearch(e)}
        onSelect={onSelect}
        options={options}
        value={value || undefined}
        notFoundContent={notFoundContent}
        // @ts-ignore
        labelRender={labelRender}
        optionRender={optionRender}
        popupClassName={popupClassName}
        onPopupScroll={onPopupScroll}
        open={open}
        onDropdownVisibleChange={onDropdownVisibleChange}
        onClear={onClear}
        suffixIcon={suffixIcon}
        onKeyDown={onKeyDown}
        dropdownRender={(menu) => (
          <>
            {menu}
            {loading && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  padding: 8px;
                  border-top: 1px solid ${colors.SECONDARY_300};
                `}
              >
                <Spin size='small' />
              </div>
            )}
          </>
        )}
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          font-size: 14px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${backgroundColor} !important;
          border-radius: 8px !important;
          height: ${SELECT_HEIGHT[size]}px !important;
          /* The <style> tag above is GLOBAL, so the last SelectInput rendered on
             the page paints every selector with its colour. Scoped to this
             instance (one class more specific), each select keeps its own. */
          .ant-select-selector {
            background: ${backgroundColor} !important;
          }
          ${size === 'small' && COMPACT_SELECT_INNER}
          ${disabled && 'opacity: 0.5 !important'}
          ${style}
        `}
        filterOption={filterOption}
        status={
          fieldErrors[name] ||
          (Array.isArray(fieldErrors) && fieldErrors?.includes(name))
            ? 'error'
            : ''
        }
      />
      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={name}
      />
    </>
  )
}
